import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { formatNumber, formatTick, placeLabels, unitOf } from './chartFormat'
import { wordsFor } from './chartWords'

/**
 * Interactive bar or line chart for one series of `{ label, value }` points.
 * A point may carry `margin`, a survey estimate's margin of error, drawn as a
 * thin range line and shown as ± in the tooltip. A chart with many points (every
 * U.S. county, say) gets a search box that narrows it to matching labels.
 *
 * Plain SVG, no chart library, drawn to the chart design standard (CLAUDE.md):
 * the `--chart-*` colors, numbers written by `chartFormat` in the unit the
 * y_label names, and category labels thinned so they never overlap. The value
 * axis includes zero, so a bar's length is never exaggerated by a cropped
 * axis; only a line chart with `zero_axis: false`, where the change is what
 * matters, fits the axis to its data, and then says so under the chart.
 * `trend_line: true` adds a dashed straight-line trend. `annotations`
 * (`[{ from, to, title, text }]`, labels of the chart) shade and number the
 * stretches they mark, with their notes listed under the chart. A line chart
 * may add `reference: { name, data }`, a benchmark series matched by label (the
 * U.S. rate next to a state's), drawn as a dashed line and named in a legend
 * with `series_name`. A line chart's point may have `value: null`, a year with
 * no figure, which breaks the line rather than joining its neighbors. Hover,
 * tap or arrow keys reveal each point's exact value; the page's data table is
 * the non-visual equivalent.
 *
 *   <ChartView chart={{ title, chart_type: 'bar', x_label, y_label, data }} />
 *
 * Pass `searchable={false}` when the page has its own search (a lookup chart).
 */

const MARGIN = { top: 16, right: 16, bottom: 28 }
const LINE_INSET = 8 // keeps a line's end markers off the plot edges
const CHAR_WIDTH = 7 // rough width of one 12px label character, for spacing decisions
const SEARCHABLE_POINTS = 60 // keep in step with Chart::SEARCHABLE_POINTS
const NOTE_ROOM = 24 // space above the plot for the annotations' numbers

/** The annotations whose labels are among `points`, as { n, from, to, title, text } with point indexes. */
export function placeAnnotations(annotations, points) {
  if (!Array.isArray(annotations)) return []
  const labels = points.map((p) => String(p.label))
  return annotations.map((note, i) => {
    const from = labels.indexOf(String(note.from))
    const to = labels.indexOf(String(note.to ?? note.from))
    return from < 0 || to < from ? null : { ...note, n: i + 1, from, to }
  }).filter(Boolean)
}

/** Whether a point has a number to draw (null, unlike Number(null), is a gap, not zero). */
const hasValue = (p) => p.value != null && p.value !== '' && Number.isFinite(Number(p.value))

/** "1986–1992", or "2023" for a single label. */
function span(note) {
  return String(note.to ?? note.from) === String(note.from) ? String(note.from) : `${note.from}–${note.to}`
}

/** A count in running text: 3,142. Chart values use `formatNumber`. */
export function formatValue(value, locale = null) {
  return new Intl.NumberFormat(locale === 'es' ? 'es-MX' : 'en-US', { maximumFractionDigits: 2 }).format(value)
}

/** Round, evenly spaced ticks covering [min, max]. */
export function niceTicks(min, max, count = 5) {
  const span = max - min || Math.abs(max) || 1
  let step = 10 ** Math.floor(Math.log10(span / count))
  const ratio = span / count / step
  if (ratio >= 7.5) step *= 10
  else if (ratio >= 3.5) step *= 5
  else if (ratio >= 1.5) step *= 2
  const start = Math.floor(min / step) * step
  const end = Math.ceil(max / step) * step
  const ticks = []
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Number(v.toPrecision(12)))
  return ticks
}

/**
 * The least-squares straight line through evenly spaced values, as its value at
 * the first and last point and its rise per step.
 */
export function trend(values) {
  const n = values.length
  if (n < 2) return null
  const meanX = (n - 1) / 2
  const meanY = values.reduce((sum, v) => sum + v, 0) / n
  let covariance = 0
  let variance = 0
  values.forEach((v, i) => {
    covariance += (i - meanX) * (v - meanY)
    variance += (i - meanX) ** 2
  })
  const slope = covariance / variance
  return { start: meanY - slope * meanX, end: meanY + slope * (n - 1 - meanX), slope }
}

/** Plain words for a trend across yearly points, e.g. "up 0.17 a decade, 2.2 in all". */
function trendWords(points, line, unit, words, locale) {
  const first = Number(points[0].label)
  const last = Number(points[points.length - 1].label)
  const yearly = Number.isInteger(first) && last - first === points.length - 1
  const change = line.end - line.start
  const size = (v) => formatNumber(Number(Math.abs(v).toPrecision(2)), unit, locale)
  return yearly
    ? words.trendYearly(first, last, change >= 0, size(line.slope * 10), size(change))
    : words.trend(change >= 0, size(change))
}

/** Bar path with 4px rounded corners on the end away from the baseline. */
function barPath(x, width, y0, y1) {
  const up = y1 <= y0
  const height = Math.abs(y0 - y1)
  const r = Math.min(4, width / 2, height)
  if (up) {
    return `M${x},${y0} V${y1 + r} Q${x},${y1} ${x + r},${y1} H${x + width - r} Q${x + width},${y1} ${x + width},${y1 + r} V${y0} Z`
  }
  return `M${x},${y0} V${y1 - r} Q${x},${y1} ${x + r},${y1} H${x + width - r} Q${x + width},${y1} ${x + width},${y1 - r} V${y0} Z`
}

function useWidth(ref, fallback = 640) {
  const [width, setWidth] = useState(fallback)
  // Measured before the first paint: drawing at the fallback width and then
  // redrawing to fit would make the page jump on a phone.
  useLayoutEffect(() => {
    const el = ref.current
    if (el?.clientWidth) setWidth(Math.max(240, el.clientWidth))
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(240, Math.floor(entry.contentRect.width))))
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])
  return width
}

export function ChartView({ chart, searchable: allowSearch = true }) {
  const containerRef = useRef(null)
  const width = useWidth(containerRef)
  const [active, setActive] = useState(null)
  const [query, setQuery] = useState('')
  const titleId = useId()
  const searchId = useId()

  const allPoints = useMemo(
    () => (Array.isArray(chart?.data)
      ? chart.data.filter((p) => p && (hasValue(p) || (chart.chart_type === 'line' && p.value === null)))
      : []),
    [chart?.data]
  )
  const searchable = allowSearch && allPoints.length > SEARCHABLE_POINTS
  const points = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle ? allPoints.filter((p) => String(p.label).toLowerCase().includes(needle)) : allPoints
  }, [allPoints, query])
  const isLine = chart?.chart_type === 'line'
  const fitAxis = isLine && chart?.zero_axis === false
  const unit = unitOf(chart?.y_label)
  const locale = chart?.locale ?? null
  const words = wordsFor(chart)
  const count = (value) => formatValue(value, locale)

  if (!allPoints.some(hasValue)) {
    return <p className="text-sm text-ink-muted">{words.noData}</p>
  }

  const search = searchable ? (
    <div className="mb-4">
      <label htmlFor={searchId} className="form-label">{words.find(chart.x_label)}</label>
      <input
        id={searchId}
        type="search"
        className="input-form-field"
        placeholder={words.placeholder}
        value={query}
        onChange={(event) => { setQuery(event.target.value); setActive(null) }}
      />
      <p className="form-hint" aria-live="polite">
        {words.showing(count(points.length), count(allPoints.length))}
      </p>
    </div>
  ) : null

  if (points.length === 0) {
    return (
      <div className="w-full">
        {search}
        <p className="text-sm text-ink-muted">{words.nothing(query.trim())}</p>
      </div>
    )
  }

  const margin = (p) => (p.margin != null && Number.isFinite(Number(p.margin)) ? Number(p.margin) : null)
  const values = points.flatMap((p) => {
    if (!hasValue(p)) return []
    const value = Number(p.value)
    const m = margin(p)
    return m == null ? [value] : [value - m, value + m]
  })
  const refValues = isLine && Array.isArray(chart.reference?.data)
    ? (() => {
        const byLabel = new Map(chart.reference.data.map((p) => [String(p.label), Number(p.value)]))
        return points.map((p) => (byLabel.has(String(p.label)) && Number.isFinite(byLabel.get(String(p.label))) ? byLabel.get(String(p.label)) : null))
      })()
    : null
  if (refValues) values.push(...refValues.filter((v) => v != null))
  const trendLine = chart.trend_line && points.length > 2 && points.every(hasValue) ? trend(points.map((p) => Number(p.value))) : null
  if (trendLine) values.push(trendLine.start, trendLine.end)
  const ticks = fitAxis
    ? niceTicks(Math.min(...values), Math.max(...values))
    : niceTicks(Math.min(0, ...values), Math.max(0, ...values))
  const yMin = ticks[0]
  const yMax = ticks[ticks.length - 1]
  const tickStep = ticks.length > 1 ? ticks[1] - ticks[0] : null
  const tickText = (t) => formatTick(t, unit, tickStep, locale)

  const notes = query.trim() ? [] : placeAnnotations(chart.annotations, points)
  const top = MARGIN.top + (notes.length ? NOTE_ROOM : 0)
  const height = (width < 480 ? 260 : 340) + (notes.length ? NOTE_ROOM : 0)
  const left = Math.max(...ticks.map((t) => tickText(t).length)) * CHAR_WIDTH + 14
  const plotWidth = width - left - MARGIN.right
  const plotHeight = height - top - MARGIN.bottom

  const y = (v) => top + ((yMax - v) / (yMax - yMin || 1)) * plotHeight
  const band = plotWidth / points.length
  const xCenter = (i) => (isLine && points.length > 1
    ? left + LINE_INSET + (i / (points.length - 1)) * (plotWidth - 2 * LINE_INSET)
    : left + band * i + band / 2)
  const baseline = y(0)
  const barWidth = Math.max(1, Math.min(band - 2, band * 0.72))
  const showRanges = (isLine && points.length > 1 ? (plotWidth - 2 * LINE_INSET) / (points.length - 1) : barWidth) >= 3

  // Show only as many category labels as fit without overlapping.
  const longest = Math.max(...points.map((p) => String(p.label).length))
  const slot = isLine && points.length > 1 ? (plotWidth - 2 * LINE_INSET) / (points.length - 1) : band
  const every = Math.max(1, Math.ceil((longest * CHAR_WIDTH + 12) / slot))
  const maxChars = Math.max(4, Math.floor((slot * every - 8) / CHAR_WIDTH))
  const shortLabel = (label) => (label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label)
  const xLabels = placeLabels(
    points.map((p, i) => (i % every === 0 ? { i, text: shortLabel(String(p.label)), x: xCenter(i) } : null)).filter(Boolean),
    width,
    CHAR_WIDTH
  )

  const indexAt = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    if (isLine && points.length > 1) {
      const t = (x - left - LINE_INSET) / (plotWidth - 2 * LINE_INSET)
      return Math.max(0, Math.min(points.length - 1, Math.round(t * (points.length - 1))))
    }
    return Math.max(0, Math.min(points.length - 1, Math.floor((x - left) / band)))
  }

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault()
      const step = event.key === 'ArrowRight' ? 1 : -1
      setActive((current) => Math.max(0, Math.min(points.length - 1, (current ?? (step > 0 ? -1 : points.length)) + step)))
    } else if (event.key === 'Escape') {
      setActive(null)
    }
  }

  const linePath = points
    .map((p, i) => (hasValue(p) ? `${i && hasValue(points[i - 1]) ? 'L' : 'M'}${xCenter(i)},${y(Number(p.value))}` : ''))
    .join(' ')
  // A figure with no neighbor on either side would be invisible as a line, so it gets a dot.
  const alone = (i) => !(points[i - 1] && hasValue(points[i - 1])) && !(points[i + 1] && hasValue(points[i + 1]))
  const refPath = refValues
    ? refValues.map((v, i) => (v == null ? '' : `${i && refValues[i - 1] != null ? 'L' : 'M'}${xCenter(i)},${y(v)}`)).join(' ')
    : null
  const activePoint = active != null ? points[active] : null
  const tooltipLeft = active != null ? Math.min(Math.max(xCenter(active), 112), width - 112) : 0
  const activeValue = activePoint ? (hasValue(activePoint) ? Number(activePoint.value) : refValues?.[active] ?? yMin) : 0
  const tooltipTop = active != null ? (fitAxis ? y(activeValue) : Math.min(y(activeValue), baseline)) : 0

  return (
    <div className="w-full">
      {search}
      {chart.y_label ? <p className="text-xs font-medium text-ink-muted mb-2">{chart.y_label}</p> : null}
      {refPath ? (
        <ul className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink" aria-label={words.legend}>
          <li className="flex items-center gap-2">
            <svg width="24" height="8" aria-hidden="true"><line x1="0" x2="24" y1="4" y2="4" stroke="var(--color-chart-series)" strokeWidth="2" /></svg>
            {chart.series_name || words.thisSeries}
          </li>
          <li className="flex items-center gap-2">
            <svg width="24" height="8" aria-hidden="true">
              <line x1="0" x2="24" y1="4" y2="4" stroke="var(--color-chart-reference)" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="6 4" />
            </svg>
            {chart.reference.name}
          </li>
        </ul>
      ) : null}
      <div ref={containerRef} className="relative w-full">
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="block max-w-full touch-pan-y outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          role="img"
          aria-labelledby={titleId}
          tabIndex={0}
          onPointerMove={(event) => setActive(indexAt(event.clientX))}
          onPointerDown={(event) => setActive(indexAt(event.clientX))}
          onPointerLeave={(event) => { if (event.pointerType === 'mouse') setActive(null) }}
          onKeyDown={onKeyDown}
          onBlur={() => setActive(null)}
        >
          <title id={titleId}>{chart.title || words.chart}</title>

          {notes.map((note) => {
            const x0 = Math.max(left, xCenter(note.from) - slot / 2)
            const x1 = Math.min(left + plotWidth, xCenter(note.to) + slot / 2)
            const cx = (x0 + x1) / 2
            return (
              <g key={`note-${note.n}`} aria-hidden="true">
                <rect x={x0} y={top} width={Math.max(2, x1 - x0)} height={plotHeight} fill="var(--color-chart-reference)" fillOpacity="0.08" />
                <line x1={cx} x2={cx} y1={top - 6} y2={top} stroke="var(--color-chart-reference)" strokeOpacity="0.5" />
                <circle cx={cx} cy={top - 15} r="9" fill="var(--color-surface)" stroke="var(--color-chart-reference)" strokeOpacity="0.7" />
                <text x={cx} y={top - 15} dy="0.35em" textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--color-chart-reference)">
                  {note.n}
                </text>
              </g>
            )
          })}

          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={left} x2={left + plotWidth} y1={y(t)} y2={y(t)}
                stroke="var(--color-chart-grid)" strokeWidth={t === 0 ? 1.5 : 1}
                strokeOpacity={t === 0 ? 1 : 0.7}
              />
              <text x={left - 8} y={y(t)} dy="0.32em" textAnchor="end" fontSize="12" fill="var(--color-chart-axis)">
                {tickText(t)}
              </text>
            </g>
          ))}

          {xLabels.map((l) => (
            <text
              key={`x-${l.i}`} x={l.x} y={height - MARGIN.bottom + 18}
              textAnchor="middle" fontSize="12" fill="var(--color-chart-axis)"
            >
              {l.text}
            </text>
          ))}

          {isLine ? (
            <>
              {active != null ? (
                <line
                  x1={xCenter(active)} x2={xCenter(active)} y1={top} y2={top + plotHeight}
                  stroke="var(--color-chart-axis)" strokeOpacity="0.5" strokeDasharray="3 3"
                />
              ) : null}
              {refPath ? (
                <path d={refPath} fill="none" stroke="var(--color-chart-reference)" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="6 4" />
              ) : null}
              <path d={linePath} fill="none" stroke="var(--color-chart-series)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {points.map((p, i) => (hasValue(p) && (points.length <= 24 || i === active || alone(i)) ? (
                <circle
                  key={`pt-${i}`} cx={xCenter(i)} cy={y(Number(p.value))} r={i === active ? 5 : 3}
                  fill="var(--color-chart-series)" stroke="var(--color-surface)" strokeWidth="2"
                />
              ) : null))}
            </>
          ) : (
            points.map((p, i) => (!hasValue(p) ? null : (
              <path
                key={`bar-${i}`}
                d={barPath(xCenter(i) - barWidth / 2, barWidth, baseline, y(Number(p.value)))}
                fill="var(--color-chart-series)"
                fillOpacity={active == null || active === i ? 1 : 0.45}
              />
            )))
          )}

          {trendLine ? (
            <line
              x1={xCenter(0)} x2={xCenter(points.length - 1)} y1={y(trendLine.start)} y2={y(trendLine.end)}
              stroke="var(--color-chart-reference)" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="6 4"
            />
          ) : null}

          {showRanges ? points.map((p, i) => {
            const m = margin(p)
            if (!m) return null
            const x = xCenter(i)
            const cap = isLine ? 4 : Math.min(4, barWidth / 3)
            const [top, bottom] = [y(Number(p.value) + m), y(Number(p.value) - m)]
            return (
              <path
                key={`moe-${i}`}
                d={`M${x},${top} V${bottom} M${x - cap},${top} H${x + cap} M${x - cap},${bottom} H${x + cap}`}
                stroke="var(--color-chart-reference)" strokeOpacity={active == null || active === i ? 0.6 : 0.25} strokeWidth="1.25"
                fill="none"
              />
            )
          }) : null}
        </svg>

        {activePoint ? (
          <div
            className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-line bg-surface px-3 py-2 text-sm shadow-card"
            style={{ left: tooltipLeft, top: tooltipTop - 8 }}
            role="status"
          >
            <div className="text-ink-muted text-xs">{chart.x_label ? `${chart.x_label}: ` : ''}{activePoint.label}</div>
            <div className="font-semibold text-ink whitespace-nowrap">
              {refValues && chart.series_name ? `${chart.series_name}: ` : ''}{hasValue(activePoint) ? formatNumber(Number(activePoint.value), unit, locale) : words.noFigure}
              {margin(activePoint) != null ? <span className="font-normal text-ink-muted"> ±{formatNumber(margin(activePoint), unit, locale)}</span> : null}
            </div>
            {refValues && refValues[active] != null ? (
              <div className="text-xs text-ink-muted whitespace-nowrap">{chart.reference.name}: {formatNumber(refValues[active], unit, locale)}</div>
            ) : null}
            {chart.y_label ? <div className="max-w-56 text-xs text-ink-muted">{chart.y_label}</div> : null}
          </div>
        ) : null}
      </div>
      {chart.x_label ? <p className="text-xs font-medium text-ink-muted text-center mt-1">{chart.x_label}</p> : null}
      {trendLine ? (
        <p className="text-xs text-ink-muted mt-2 flex items-start gap-2">
          <svg width="24" height="8" className="mt-1 shrink-0" aria-hidden="true">
            <line x1="0" x2="24" y1="4" y2="4" stroke="var(--color-chart-reference)" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="6 4" />
          </svg>
          <span>{words.dashedLine} {trendWords(points, trendLine, unit, words, locale)}</span>
        </p>
      ) : null}
      {notes.length ? (
        <ol className="mt-3 space-y-2 text-sm text-ink">
          {notes.map((note) => (
            <li key={`note-text-${note.n}`} className="flex items-start gap-2">
              <span
                className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-chart-reference text-xs font-semibold"
                aria-hidden="true"
              >
                {note.n}
              </span>
              <span className="min-w-0 break-words">
                <span className="font-semibold">{note.title}</span> <span className="text-ink-muted">({span(note)})</span>: {note.text}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {fitAxis && (yMin > 0 || yMax < 0) ? (
        <p className="text-xs text-ink-muted mt-2">
          {words.axisStart(tickText(yMin))}
        </p>
      ) : null}
    </div>
  )
}
